<template lang="pug">
.c-directory-child-page
  //- Action buttons for parent slot
  teleport(to=".c-directory-page__nav-actions" v-if="mounted")
    el-button.c-directory-action-btn(@click="exportValues")
      fai(type="fal" icon="download")
      span Export
    el-button.c-directory-action-btn(@click="dialog = true")
      fai(type="fal" icon="plus-circle")
      span Add New

  teleport(to=".c-directory-page__nav-filters" v-if="mounted")
    DirectoryFilters(
      v-model="filter"
      :sort-value="sortBy"
      :sort-options="sortOptions"
      placeholder="Search values..."
      @search="handleSearch"
      @update:sortValue="sortBy = $event"
      @sort="sortTable"
    )

  .c-directory-content
    //- Table Section
    section.c-directory-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--star
            .c-skeleton-cell.c-skeleton-cell--value
            .c-skeleton-cell.c-skeleton-cell--description
      
      //- Table
      el-table(
        v-else
        :data="pagedTableData"
        @row-click="$router.push(`values/${$event.id}`)"
        style="width: 100%"
      ).clickable-rows
        el-table-column(width="15")
        
        el-table-column(width="80")
          template(#default="scope")
            div(@click.stop="handleToggleAddToFav(scope.row)").favorite-icon
              img(v-if="scope.row.favorite" src="@/assets/icons/star-green.svg").icon-star
              img(v-else src="@/assets/icons/star.svg").icon-star

        el-table-column(prop="value" label="Value" min-width="200")
          template(#default="scope")
            .lh-sm
              .font-black {{ scope.row.value }}
        
        el-table-column(prop="description" label="Description" min-width="250")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.description }}

        el-table-column(width="15")

      //- Pagination
      .c-directory-pagination
        el-pagination(
          layout="prev, pager, next"
          v-model:current-page="page"
          :total="filtered.length"
          @current-change="setPage"
        )

  dialog-add-value(
    v-if="dialog"
    :loading="loading"
    v-model="dialog"
    @save-item="saveItem"
    @close-dialog="dialog = false"
  )
</template>

<script lang="ts">

 
import { defineComponent, ref, onMounted, reactive, computed } from 'vue';
import shared from './shared';
import DialogAddValue from './DialogAddValue.vue';
import DirectoryFilters from '../DirectoryFilters.vue';
import globalShared from '@/shared';

export default defineComponent({
  name: 'ManageContent_Directory_Values',
  components: { DialogAddValue, DirectoryFilters },
  setup() {
    const {
      loading,
      getValues,
      saveValue,
      exportData,
    } = shared();

    const { showSuccessMessage } = globalShared();

    // Initialize loading as true to show skeleton immediately
    loading.value = true;

    const mounted = ref(false);
    const sortBy = ref('');
    const sortOptions = [{ label: 'Value', value: 'value' }];

    const filter = ref('');
    const items = reactive({ items: [] });
    const dialog = ref(false);
    const page = ref(1);
    const pageSize = 10;

    async function getItems() {
      try {
        loading.value = true;
        items.items = await getValues(false);
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    async function saveItem(input) {
      await saveValue(input);
      showSuccessMessage('Value added successfully');
      getItems();
    }

    function sortTable() {
      const sortByValue = sortBy.value.value;

      items.items.sort((a, b) => {
        if (sortByValue === 'value') {
          return a.value.localeCompare(b.value);
        }
        return 0;
      });
    }

    const filtered = computed(() => {
      if (filter.value === '') {
        return items.items;
      }
      const regex = new RegExp(`${filter.value}`, 'gi');
      return items.items.filter((x) => {
        if (x.value) {
          return x.value.match(regex);
        }
        return false;
      });
    });

    function exportValues() {
      exportData(items.items);
    }

    function handleSearch() {
      // Search is handled by computed filtered property
    }

    // Pagination datatable
    const pagedTableData = computed(() => {
      const start = (page.value - 1) * pageSize;
      const end = start + pageSize;
      if (filter.value !== '') {
        return filtered.value.slice(start, end);
      }

      return items.items.slice(start, end);
    });

    const setPage = (newPage) => {
      page.value = newPage;
    };

    onMounted(async () => {
      mounted.value = true;
      await getItems();
    });

    async function handleToggleAddToFav(row) {
      const itemToFav = filtered.value.find((obj) => obj.id === row.id);
      itemToFav.favorite = !itemToFav.favorite;
      await saveValue({
        id: parseInt(itemToFav.id, 10),
        favorite: itemToFav.favorite,
      });
    }

    return {
      mounted,
      items,
      filter,
      filtered,
      loading,
      dialog,
      sortBy,
      sortOptions,
      page,
      pageSize,
      pagedTableData,
      // methods
      saveItem,
      exportValues,
      handleToggleAddToFav,
      setPage,
      sortTable,
      handleSearch,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-directory-child-page {
  width: 100%;
}

.c-directory-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Action button styles - Match Invitations page
.c-directory-action-btn {
  height: 36px;
  font-size: 14px;
  border-radius: 8px;
  background-color: white !important;
  border: 1.5px solid $color-purple-text !important;
  color: $color-purple-text !important;

  &:hover,
  &:focus {
    background-color: rgba($color-purple-text, 0.08) !important;
    border-color: color.adjust($color-purple-text, $lightness: -8%) !important;
    color: color.adjust($color-purple-text, $lightness: -8%) !important;
  }

  :deep(span) {
    font-weight: 500;
  }

  :deep(.me-2) {
    margin-right: 0.5rem;
  }
}

// Filters Section

// Table Section
.c-directory-table-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  overflow: hidden;

  :deep(.el-table) {
    min-width: 100%;
    border-radius: 0;

    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination - Outlined style
.c-directory-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1rem;
  border-top: 1px solid $gray-100;

  :deep(.el-pagination) {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .btn-prev,
    .btn-next {
      background: transparent;
      border: 1px solid $gray-300;
      border-radius: 8px;
      padding: 8px 12px;
      font-weight: 500;
      color: $gray-700;
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        border-color: $color-purple-text;
        color: $color-purple-text;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .el-pager {
      display: flex;
      gap: 0.25rem;

      li {
        min-width: 36px;
        height: 36px;
        line-height: 36px;
        border-radius: 8px;
        font-weight: 500;
        color: $gray-700;
        background: transparent;
        border: 1px solid $gray-300;
        transition: all 0.2s ease;

        &:hover {
          border-color: $color-purple-text;
          color: $color-purple-text;
        }

        &.is-active {
          background: transparent;
          border-color: $color-purple-text;
          color: $color-purple-text;
          font-weight: 600;
        }
      }
    }
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Favorite icon
.favorite-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
}

.icon-star {
  width: 24px;
  height: 24px;
}

// Skeleton Loader
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--star {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &--value {
    width: 180px;
    flex-shrink: 0;
  }

  &--description {
    flex: 1;
    min-width: 200px;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Responsive Design
@media (max-width: 768px) {
  .c-directory-pagination {
    padding: 1rem 0.5rem;
  }
}
</style>
